class ProductPage extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.stockThreshold = parseInt(this.dataset.stockThreshold, 10) || 3;
  }

  async connectedCallback() {
    this.form = this.querySelector('[data-product-form]');
    this.variantIdInput = this.querySelector('[data-variant-id-input]');

    // Real inventory_quantity isn't exposed by /products/{handle}.js (Shopify
    // withholds it from the public AJAX API) — read it from the Liquid-embedded
    // data instead, which has access to the real per-variant numbers.
    this.variantInventory = {};
    const inventoryEl = this.querySelector('[data-variant-inventory]');
    if (inventoryEl) {
      try {
        this.variantInventory = JSON.parse(inventoryEl.textContent);
      } catch (e) {
        console.error('Product Page: failed to parse embedded inventory data', e);
      }
    }

    // Pull full product JSON (variants, options, inventory) once.
    const handle = this.dataset.productHandle;
    try {
      const res = await fetch(`/products/${handle}.js`);
      this.product = await res.json();
    } catch (e) {
      console.error('Product Page: failed to load product JSON', e);
      return;
    }

    this.selectedOptions = [...this.product.options]; // defaults to first variant's option values
    const initialVariant = this.product.variants.find(v => v.available) || this.product.variants[0];
    this.selectedOptions = initialVariant.options.slice();

    this.initGallery();
    this.initOptionPickers();
    this.initAccordion();
    this.initSizeGuide();
    this.initStickyFooterSheet();
    this.initAddToCart();
    this.initWishlist();
    this.initImageModal();

    this.updateVariantUI(this.getMatchingVariant());
  }

  // ---------------- Variant matching ----------------
  getMatchingVariant() {
    return this.product.variants.find(v =>
      v.options.every((val, idx) => val === this.selectedOptions[idx])
    );
  }

  updateVariantUI(variant) {
    const priceEl = this.querySelector('[data-product-price]');
    const compareEl = this.querySelector('[data-compare-price]');
    const stockWarn = this.querySelector('[data-stock-warning]');
    const stockCount = this.querySelector('[data-stock-count]');
    const addBtn = this.querySelector('[data-add-to-cart]');
    const addBtnMobile = this.querySelector('[data-add-to-cart-mobile]');
    const addBtnLabel = this.querySelector('[data-add-btn-label]');

    if (!variant) {
      if (addBtn) addBtn.disabled = true;
      if (addBtnMobile) addBtnMobile.disabled = true;
      if (addBtnLabel) addBtnLabel.textContent = 'Unavailable';
      return;
    }

    this.variantIdInput.value = variant.id;
    if (priceEl) priceEl.textContent = this.formatMoney(variant.price);
    if (compareEl) {
      if (variant.compare_at_price > variant.price) {
        compareEl.textContent = this.formatMoney(variant.compare_at_price);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }

    const available = variant.available;
    if (addBtn) addBtn.disabled = !available;
    if (addBtnMobile) addBtnMobile.disabled = !available;
    if (addBtnLabel) addBtnLabel.textContent = available ? 'Add to Bag' : 'Sold Out';

    // Stock threshold: per-product metafield overrides section default, read from data attr set in liquid if present
    const threshold = this.stockThreshold;
    const inventoryInfo = this.variantInventory[variant.id] || {};
    const rawQty = inventoryInfo.inventory_quantity;
    const qty = typeof rawQty === 'string' ? Number(rawQty) : rawQty;
    const tracksInventory = typeof qty === 'number' && !Number.isNaN(qty);
    const showWarning = tracksInventory && qty <= threshold;

    if (stockWarn) {
      stockWarn.hidden = !showWarning;
      if (showWarning && stockCount) stockCount.textContent = Math.max(qty, 0);
    }

    // Jump gallery to the featured image tied to this variant, if set
    if (variant.featured_media) {
      this.scrollToMediaId(variant.featured_media.id);
    }

    // Update option-availability styling (disable combos with no matching available variant)
    this.refreshOptionAvailability();

    // Sync footer labels
    this.selectedOptions.forEach((val, idx) => {
      const sfLabel = this.querySelector(`[data-sf-label="${idx}"]`);
      if (sfLabel) sfLabel.textContent = val;
      const selLabel = this.querySelectorAll('[data-selected-option-label]')[idx];
      if (selLabel) selLabel.textContent = val;
    });
  }

  refreshOptionAvailability() {
    this.querySelectorAll('[data-option-name]').forEach((group, optionIndex) => {
      group.querySelectorAll('.opt').forEach(optEl => {
        const value = optEl.dataset.optionValue;
        const testOptions = this.selectedOptions.slice();
        testOptions[optionIndex] = value;
        const match = this.product.variants.find(v =>
          v.options.every((val, idx) => val === testOptions[idx])
        );
        optEl.classList.toggle('unavailable', !match || !match.available);
      });
    });
  }

  formatMoney(cents) {
    // Basic fallback formatter; theme's Shopify.formatMoney (money_format) takes precedence if present.
    if (window.Shopify && Shopify.formatMoney && window.themeMoneyFormat) {
      return Shopify.formatMoney(cents, window.themeMoneyFormat);
    }
    return '₦' + (cents / 100).toLocaleString();
  }

  // ---------------- Option pickers (desktop buybox + mobile sheet share this data) ----------------
  initOptionPickers() {
    this.querySelectorAll('[data-option-name]').forEach((group, optionIndex) => {
      group.addEventListener('click', (e) => {
        const optEl = e.target.closest('.opt');
        if (!optEl || optEl.classList.contains('unavailable')) return;
        this.selectedOptions[optionIndex] = optEl.dataset.optionValue;
        group.querySelectorAll('.opt').forEach(o => o.classList.toggle('selected', o === optEl));
        this.updateVariantUI(this.getMatchingVariant());
        this.syncSheetIfOpen(optionIndex);
      });
    });
  }

  // ---------------- Mobile bottom sheet ----------------
  initStickyFooterSheet() {
    this.overlay = this.querySelector('[data-sheet-overlay]');
    this.sheet = this.querySelector('[data-sheet]');
    this.sheetContent = this.querySelector('[data-sheet-content]');

    this.querySelectorAll('[data-sheet-trigger]').forEach(btn => {
      btn.addEventListener('click', () => {
        const optionIndex = parseInt(btn.dataset.sheetTrigger, 10);
        this.openSheet(optionIndex);
      });
    });

    this.overlay?.addEventListener('click', () => this.closeSheet());
  }

  openSheet(optionIndex) {
    const option = optionName(this.product.options[optionIndex]);
    const group = this.querySelectorAll('[data-option-name]')[optionIndex];
    const values = [...group.querySelectorAll('.opt')].map(o => ({
      value: o.dataset.optionValue,
      unavailable: o.classList.contains('unavailable'),
      selected: o.classList.contains('selected'),
    }));

    this.sheetContent.innerHTML = `
      <h3>Select ${option}</h3>
      <div class="opt-grid" data-sheet-option-index="${optionIndex}">
        ${values.map(v => `
          <div class="opt ${v.unavailable ? 'unavailable' : ''} ${v.selected ? 'selected' : ''}"
               data-option-value="${v.value}">${v.value}</div>
        `).join('')}
      </div>
      <button class="sheet-done" data-sheet-done>Done</button>
    `;

    this.sheetContent.querySelector('[data-sheet-option-index]').addEventListener('click', (e) => {
      const optEl = e.target.closest('.opt');
      if (!optEl || optEl.classList.contains('unavailable')) return;
      this.selectedOptions[optionIndex] = optEl.dataset.optionValue;
      const desktopGroup = this.querySelectorAll('[data-option-name]')[optionIndex];
      desktopGroup.querySelectorAll('.opt').forEach(o =>
        o.classList.toggle('selected', o.dataset.optionValue === optEl.dataset.optionValue)
      );
      this.updateVariantUI(this.getMatchingVariant());
      this.openSheet(optionIndex); // refresh sheet state
    });

    this.sheetContent.querySelector('[data-sheet-done]').addEventListener('click', () => this.closeSheet());

    this.overlay.classList.add('show');
    this.sheet.classList.add('show');
    this.activeSheetOption = optionIndex;
  }

  syncSheetIfOpen(optionIndex) {
    if (this.activeSheetOption === optionIndex && this.sheet.classList.contains('show')) {
      this.openSheet(optionIndex);
    }
  }

  closeSheet() {
    this.overlay.classList.remove('show');
    this.sheet.classList.remove('show');
  }

  // ---------------- Gallery ----------------
  initGallery() {
    this.slidesEl = this.querySelector('[data-gallery-slides]');
    this.dots = [...this.querySelectorAll('[data-dot-index]')];
    this.thumbs = [...this.querySelectorAll('[data-thumb-index]')];
    this.slides = [...this.querySelectorAll('.slide')];

    this.dots.forEach(dot => {
      dot.addEventListener('click', () => this.scrollToSlideIndex(parseInt(dot.dataset.dotIndex, 10)));
    });
    this.thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => this.scrollToSlideIndex(parseInt(thumb.dataset.thumbIndex, 10)));
    });

    this.slidesEl?.addEventListener('scroll', () => {
      const i = Math.round(this.slidesEl.scrollLeft / this.slidesEl.clientWidth);
      this.setActiveSlide(i);
    });
  }

  scrollToSlideIndex(i) {
    this.slidesEl.scrollTo({ left: this.slidesEl.clientWidth * i, behavior: 'smooth' });
    this.setActiveSlide(i);
  }

  scrollToMediaId(mediaId) {
    const idx = this.slides.findIndex(s => s.dataset.mediaId == mediaId);
    if (idx > -1) this.scrollToSlideIndex(idx);
  }

  setActiveSlide(i) {
    this.dots.forEach((d, idx) => d.classList.toggle('active', idx === i));
    this.thumbs.forEach((t, idx) => t.classList.toggle('active', idx === i));
  }

  // ---------------- Accordion ----------------
  initAccordion() {
    this.querySelectorAll('[data-acc-toggle]').forEach(btn => {
      btn.addEventListener('click', () => {
        const item = btn.closest('.acc-item');
        const wasOpen = item.classList.contains('open');
        this.querySelectorAll('.acc-item').forEach(i => i.classList.remove('open'));
        if (!wasOpen) item.classList.add('open');
      });
    });
  }

  // ---------------- Size guide (unit toggle + dynamic table) ----------------
  initSizeGuide() {
    const dataEl = this.querySelector('[data-size-chart-data]');
    if (!dataEl) return;

    let chart;
    try {
      const parsed = JSON.parse(dataEl.textContent);
      chart = {
        columns: parsed.columns || ['Size'],
        rows_cm: parsed.rows_cm || {},
        rows_in: parsed.rows_in || {},
        rows_intl: parsed.rows_intl || {},
        intl_columns: parsed.intl_columns || ['Size', 'Intl'],
      };
    } catch (e) {
      console.error('Product Page: invalid size chart JSON', e);
      return;
    }

    this.sizeChart = chart;
    this.tableEl = this.querySelector('[data-size-table]');
    this.unitTabs = [...this.querySelectorAll('[data-unit-tab]')];

    this.unitTabs.forEach(tab => {
      tab.addEventListener('click', () => {
        this.unitTabs.forEach(t => t.classList.toggle('active', t === tab));
        this.renderSizeTable(tab.dataset.unitTab);
      });
    });

    const initialUnit = this.unitTabs.find(t => t.classList.contains('active'))?.dataset.unitTab;
    if (initialUnit) this.renderSizeTable(initialUnit);
  }

  renderSizeTable(unit) {
    if (!this.tableEl || !this.sizeChart) return;

    const isIntl = unit === 'intl';
    const columns = isIntl ? this.sizeChart.intl_columns : this.sizeChart.columns;
    const rows = isIntl ? this.sizeChart.rows_intl : (unit === 'cm' ? this.sizeChart.rows_cm : this.sizeChart.rows_in);

    if (!rows || !Object.keys(rows).length) {
      this.tableEl.innerHTML = '<tbody><tr><td>Size chart unavailable.</td></tr></tbody>';
      return;
    }

    const head = `<thead><tr>${columns.map(c => `<th>${c}</th>`).join('')}</tr></thead>`;
    const body = `<tbody>${Object.entries(rows).map(([size, values]) => {
      const cells = Array.isArray(values) ? values : [values];
      return `<tr><td>${size}</td>${cells.map(v => `<td>${v}</td>`).join('')}</tr>`;
    }).join('')}</tbody>`;

    this.tableEl.innerHTML = head + body;
  }

  // ---------------- Add to cart ----------------
  initImageModal() {
    this.modalOverlay = this.querySelector('[data-img-modal-overlay]');
    this.modal = this.querySelector('[data-img-modal]');
    this.modalImg = this.querySelector('[data-img-modal-img]');
    const closeBtn = this.querySelector('[data-img-modal-close]');
    if (!this.modal || !this.modalImg) return;

    // Open on click — delegated, covers both the gallery slides and UGC strip cards
    this.addEventListener('click', (e) => {
      const slide = e.target.closest('[data-media-id]');
      const ugcCard = e.target.closest('[data-ugc-image]');
      const source = slide || ugcCard;
      if (!source) return;
      // Don't hijack clicks on gallery dots/thumbs or quick-add controls
      if (e.target.closest('[data-quick-add-trigger], [data-quick-add-panel]')) return;

      const fullSrc = source.dataset.fullSrc;
      if (!fullSrc) return;

      e.preventDefault();
      this.openImageModal(fullSrc);
    });

    closeBtn?.addEventListener('click', () => this.closeImageModal());
    this.modalOverlay?.addEventListener('click', () => this.closeImageModal());

    // The modal itself sits above the overlay and covers the full screen too,
    // so clicks in the empty space around the image (not the image itself,
    // not the close button) need their own handler to actually close it.
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal || e.target.closest('[data-img-modal-inner]') === e.target) {
        this.closeImageModal();
      }
    });

    // Click image to toggle zoom; while zoomed, mouse position pans the image
    this.modalImg.addEventListener('click', (e) => {
      e.stopPropagation();
      const isZoomed = this.modalImg.classList.toggle('zoomed');
      if (isZoomed) {
        this.setZoomOrigin(e);
      } else {
        this.modalImg.style.transformOrigin = 'center center';
      }
    });

    this.modalImg.addEventListener('mousemove', (e) => {
      if (this.modalImg.classList.contains('zoomed')) this.setZoomOrigin(e);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('show')) this.closeImageModal();
    });
  }

  setZoomOrigin(e) {
    const rect = this.modalImg.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    this.modalImg.style.transformOrigin = `${x}% ${y}%`;
  }

  openImageModal(src) {
    this.modalImg.src = src;
    this.modalImg.classList.remove('zoomed');
    this.modalImg.style.transformOrigin = 'center center';
    this.modalOverlay.classList.add('show');
    this.modal.classList.add('show');
    document.body.style.overflow = 'hidden';
  }

  closeImageModal() {
    this.modalOverlay.classList.remove('show');
    this.modal.classList.remove('show');
    this.modalImg.classList.remove('zoomed');
    document.body.style.overflow = '';
  }

  initWishlist() {
    const btn = this.querySelector('[data-wishlist-toggle]');
    if (!btn) return;
    const icon = btn.querySelector('[data-wishlist-icon]');

    btn.addEventListener('click', () => {
      const isActive = btn.classList.toggle('active');
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      if (icon) icon.textContent = isActive ? '♥' : '♡';
    });
  }

  initAddToCart() {
    const submit = async () => {
      const variant = this.getMatchingVariant();
      if (!variant) return;
      if (!variant.available) return;

      const formData = new FormData(this.form);

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });
        if (!res.ok) throw new Error('Add to cart failed');
        const item = await res.json();

        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item } }));

        const opened = await refreshCartAndOpenDrawer();
        if (!opened) window.location.href = '/cart';
      } catch (err) {
        console.error(err);
        alert('Something went wrong adding this to your bag. Please try again.');
      }
    };

    this.querySelector('[data-add-to-cart]')?.addEventListener('click', (e) => {
      e.preventDefault();
      submit();
    });
    this.querySelector('[data-add-to-cart-mobile]')?.addEventListener('click', (e) => {
      e.preventDefault();
      submit();
    });
  }
}

customElements.define('product-page', ProductPage);

// This store's custom cart drawer (sections/svr-cart-drawer.liquid) exposes
// a global window.svrCartOpen() that opens the drawer AND fetches /cart.js
// itself to render items + suggestions — so we just need to call it.
function refreshCartAndOpenDrawer() {
  if (typeof window.svrCartOpen === 'function') {
    window.svrCartOpen();
    return true;
  }
  return false;
}

// The drawer dispatches a `cart:updated` event with { count } after every
// render, but the header (sections/header.liquid) has no listener for it —
// its cart badge is static server-rendered HTML. Wire it up once here so
// the header count stays in sync whenever the drawer updates.
if (!window.__svrHeaderBadgeWired) {
  window.__svrHeaderBadgeWired = true;
  document.addEventListener('cart:updated', (e) => {
    const count = e.detail && typeof e.detail.count === 'number' ? e.detail.count : null;
    if (count === null) return;

    const cartLink = document.querySelector('.hdr__icon[aria-label="Cart"]');
    if (!cartLink) return;

    let badge = cartLink.querySelector('.hdr__cart-badge');
    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'hdr__cart-badge';
        cartLink.appendChild(badge);
      }
      badge.setAttribute('data-count', count);
      badge.textContent = count;
    } else if (badge) {
      badge.remove();
    }
  });
}

// Shopify's /products/{handle}.js can return `options` as either plain
// strings (["Size","Color"]) or objects ({name:"Size",...}) depending on
// API version — normalize so downstream code never breaks on this.
function optionName(o) {
  if (typeof o === 'string') return o;
  if (o && typeof o === 'object' && 'name' in o) return o.name;
  return String(o);
}

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    this.productCache = new Map();
    const url = this.dataset.url;
    if (!url) return;

    fetch(url)
      .then(res => res.text())
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const grid = doc.querySelector('[data-recommendations-grid]');

        if (grid && grid.children.length) {
          this.innerHTML = grid.innerHTML;
          this.initQuickAdd();
          this.initArrows();
        } else {
          this.closest('[data-recommendations-wrapper]')?.remove();
        }
      })
      .catch(err => {
        console.error('Product recommendations fetch failed', err);
        this.closest('[data-recommendations-wrapper]')?.remove();
      });
  }

  initArrows() {
    const wrapper = this.closest('[data-recommendations-wrapper]');
    const prevBtn = wrapper?.querySelector('[data-rec-prev]');
    const nextBtn = wrapper?.querySelector('[data-rec-next]');
    if (!prevBtn || !nextBtn) return;

    const scrollByCard = (direction) => {
      const card = this.querySelector('.rel-card');
      const distance = card ? card.getBoundingClientRect().width + 16 : this.clientWidth * 0.8;
      this.scrollBy({ left: distance * direction, behavior: 'smooth' });
    };

    prevBtn.addEventListener('click', () => scrollByCard(-1));
    nextBtn.addEventListener('click', () => scrollByCard(1));

    const updateArrowState = () => {
      const maxScroll = this.scrollWidth - this.clientWidth - 1;
      prevBtn.disabled = this.scrollLeft <= 0;
      nextBtn.disabled = this.scrollLeft >= maxScroll;
    };

    this.addEventListener('scroll', updateArrowState);
    window.addEventListener('resize', updateArrowState);
    updateArrowState();
  }

  initQuickAdd() {
    this.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-quick-add-trigger]');
      const sizeBtn = e.target.closest('.qa-size');
      const closeBtn = e.target.closest('.qa-close');
      const panel = e.target.closest('[data-quick-add-panel]');

      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        this.openQuickAdd(trigger.closest('[data-quick-add-card]'));
        return;
      }
      if (sizeBtn && !sizeBtn.classList.contains('unavailable')) {
        e.preventDefault();
        e.stopPropagation();
        this.addQuickAdd(sizeBtn);
        return;
      }
      if (closeBtn) {
        e.preventDefault();
        e.stopPropagation();
        this.closeQuickAdd(closeBtn.closest('[data-quick-add-card]'));
        return;
      }
      if (panel) {
        // clicks anywhere else inside the panel shouldn't navigate
        e.preventDefault();
        e.stopPropagation();
      }
    });
  }

  async openQuickAdd(card) {
    const handle = card.dataset.productHandle;
    const trigger = card.querySelector('[data-quick-add-trigger]');
    const panel = card.querySelector('[data-quick-add-panel]');

    trigger.hidden = true;
    panel.hidden = false;
    panel.innerHTML = `<div class="qa-label">Loading…</div>`;

    try {
      let product = this.productCache.get(handle);
      if (!product) {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) throw new Error(`Product fetch failed: ${res.status}`);
        product = await res.json();
        this.productCache.set(handle, product);
      }

      const options = product.options || [];
      const variants = product.variants || [];
      const sizeOptionIndex = options.findIndex(o => optionName(o).toLowerCase() === 'size');

      if (sizeOptionIndex === -1) {
        // No size option — single variant, just add directly
        const variant = variants[0];
        if (!variant) throw new Error('No variants found on product');

        panel.innerHTML = `
          <button class="qa-close" type="button" aria-label="Close">✕</button>
          <div class="qa-label">${product.title}</div>
          <div class="qa-sizes">
            <button class="qa-size" data-variant-id="${variant.id}">
              ${variant.available ? 'Add to Bag' : 'Sold Out'}
            </button>
          </div>
        `;
        return;
      }

      const sizes = [...new Set(variants.map(v => v.options[sizeOptionIndex]))];
      panel.innerHTML = `
        <button class="qa-close" type="button" aria-label="Close">✕</button>
        <div class="qa-label">Select size</div>
        <div class="qa-sizes">
          ${sizes.map(size => {
            const variant = variants.find(v => v.options[sizeOptionIndex] === size);
            const available = variant && variant.available;
            return `<button class="qa-size ${!available ? 'unavailable' : ''}"
                      data-variant-id="${variant ? variant.id : ''}">${size}</button>`;
          }).join('')}
        </div>
      `;
    } catch (err) {
      console.error('Quick add failed:', err);
      panel.innerHTML = `
        <button class="qa-close" type="button" aria-label="Close">✕</button>
        <div class="qa-label">Couldn't load sizes. Please try the product page instead.</div>
      `;
    }
  }

  async addQuickAdd(sizeBtn) {
    const variantId = sizeBtn.dataset.variantId;
    if (!variantId) return;

    const panel = sizeBtn.closest('[data-quick-add-panel]');
    sizeBtn.textContent = 'Adding…';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });
      if (!res.ok) throw new Error('Add to cart failed');
      const item = await res.json();

      sizeBtn.textContent = 'Added ✓';
      sizeBtn.classList.add('added');
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item } }));

      const opened = await refreshCartAndOpenDrawer();

      setTimeout(() => {
        const card = sizeBtn.closest('[data-quick-add-card]');
        if (card) this.closeQuickAdd(card);
        if (!opened) window.location.href = '/cart';
      }, 500);
    } catch (err) {
      console.error(err);
      sizeBtn.textContent = 'Error — retry';
    }
  }

  closeQuickAdd(card) {
    const trigger = card.querySelector('[data-quick-add-trigger]');
    const panel = card.querySelector('[data-quick-add-panel]');
    panel.hidden = true;
    panel.innerHTML = '';
    trigger.hidden = false;
    trigger.textContent = 'Quick Add';
  }
}

customElements.define('product-recommendations', ProductRecommendations);